class ContactModalButton extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('.contact-modal-button');
    this.hideReasons = new Set();
    this.lastScrollY = window.scrollY;
    this.scrollThreshold = 50;

    // Get configuration from data attributes
    this.hideSelectors = this.getHideSelectors();
    this.hideUrls = this.getHideUrls();
    this.rootMargin = this.getAttribute('data-root-margin') || '-80% 100px 100px -80%';

    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.checkVisibility = this.checkVisibility.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  getHideSelectors() {
    const selectorsAttr = this.getAttribute('data-hide-selectors');
    if (!selectorsAttr || selectorsAttr.trim() === '') return [];

    // Split by custom delimiter and filter out empty strings
    return selectorsAttr
      .split('|||')
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  getHideUrls() {
    const urlsAttr = this.getAttribute('data-hide-urls');
    if (!urlsAttr || urlsAttr.trim() === '') return [];

    // Split by custom delimiter and filter out empty strings
    return urlsAttr
      .split('|||')
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  init() {
    // Check if current URL matches hide URLs
    if (this.shouldHideOnCurrentUrl()) {
      this.hide('url');
      return;
    }

    // Set up observers and listeners
    this.setupIntersectionObservers();
    this.setupCartDrawerObserver();
    this.setupScrollListener();
    this.setupResizeListener();

    // Initial visibility check
    this.checkVisibility();
  }

  shouldHideOnCurrentUrl() {
    const currentPath = window.location.pathname;
    return this.hideUrls.some(urlPattern => {
      // Support wildcards
      const regex = new RegExp('^' + urlPattern.replace(/\*/g, '.*') + '$');
      return regex.test(currentPath);
    });
  }

  setupIntersectionObservers() {
    if (this.hideSelectors.length === 0) return;

    this.observers = [];

    this.hideSelectors.forEach((selector, index) => {
      const elements = document.querySelectorAll(selector);

      if (elements.length === 0) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const reasonKey = `selector-${index}`;

          // Only hide when element overlaps with bottom-right corner where button sits
          // The rootMargin creates a box in the bottom-right that represents the button area + padding
          if (entry.isIntersecting && entry.intersectionRatio > 0) {
            this.hide(reasonKey);
          } else {
            this.show(reasonKey);
          }
        });
      }, {
        root: null,
        // Focus on bottom-right corner: add 100px padding on bottom and right
        // Negative top and left values mean we only watch the bottom-right area
        rootMargin: this.rootMargin,
        threshold: [0, 0.1, 0.5, 0.9, 1.0]
      });

      elements.forEach(el => observer.observe(el));
      this.observers.push(observer);
    });
  }

  setupCartDrawerObserver() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    this.cartObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isActive = cartDrawer.classList.contains('active');
          if (isActive) {
            this.hide('cart');
          } else {
            this.show('cart');
          }
        }
      });
    });

    this.cartObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Check initial state
    if (cartDrawer.classList.contains('active')) {
      this.hide('cart');
    }
  }

  setupScrollListener() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
  }

  setupResizeListener() {
    window.addEventListener('resize', this.handleResize, { passive: true });
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibility();
    }, 150);
  }

  handleScroll() {
    clearTimeout(this.scrollTimeout);

    // Check for homepage hero scroll requirement
    const isHomepage = window.location.pathname === '/' || window.location.pathname === '';
    if (isHomepage) {
      const scrolledPastThreshold = window.scrollY > 100;
      if (!scrolledPastThreshold) {
        this.hide('hero-scroll');
      } else {
        this.show('hero-scroll');
      }
    }

    // Debounce
    this.scrollTimeout = setTimeout(() => {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - this.lastScrollY;

      if (Math.abs(scrollDelta) > this.scrollThreshold) {
        this.lastScrollY = currentScrollY;
        this.checkVisibility();
      }
    }, 50);
  }

  hide(reason) {
    this.hideReasons.add(reason);
    this.updateVisibility();
  }

  show(reason) {
    this.hideReasons.delete(reason);
    this.updateVisibility();
  }

  updateVisibility() {
    if (!this.button) return;

    const shouldBeVisible = this.hideReasons.size === 0;

    if (shouldBeVisible) {
      this.button.classList.add('is-visible');
    } else {
      this.button.classList.remove('is-visible');
    }
  }

  checkVisibility() {
    this.updateVisibility();
  }

  cleanup() {
    // Remove event listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    // Disconnect observers
    if (this.observers) {
      this.observers.forEach(observer => observer.disconnect());
    }
    if (this.cartObserver) this.cartObserver.disconnect();

    // Clear timeouts
    clearTimeout(this.scrollTimeout);
    clearTimeout(this.resizeTimeout);
  }
}

customElements.define('contact-modal-wrapper', ContactModalButton);
